import type { MenuProps } from "@mui/material/Menu";
import { LAYERS } from "./layering";

export const OVERLAY_SAFE_POPUP_Z_INDEX = LAYERS.dropdown;

const OVERLAY_SAFE_Z_INDEX_STYLE = {
  zIndex: OVERLAY_SAFE_POPUP_Z_INDEX,
} as const;

const OVERLAY_SAFE_Z_INDEX_SX = {
  zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
} as const;

/**
 * Shared menu layering config to keep Select/Menu popups above app overlays
 * and avoid profile-specific portal stacking inconsistencies.
 */
const getBaseMenuProps = (): Partial<MenuProps> => ({
  style: OVERLAY_SAFE_Z_INDEX_STYLE,
  sx: OVERLAY_SAFE_Z_INDEX_SX,
  slotProps: {
    root: {
      style: OVERLAY_SAFE_Z_INDEX_STYLE,
      sx: OVERLAY_SAFE_Z_INDEX_SX,
    },
    paper: {
      style: OVERLAY_SAFE_Z_INDEX_STYLE,
      sx: OVERLAY_SAFE_Z_INDEX_SX,
    },
  },
  PaperProps: {
    style: OVERLAY_SAFE_Z_INDEX_STYLE,
    sx: OVERLAY_SAFE_Z_INDEX_SX,
  },
});

const getPortalMenuProps = (): Partial<MenuProps> => ({
  ...getBaseMenuProps(),
  disablePortal: false,
  container: typeof document !== "undefined" ? document.body : undefined,
});

const getDialogMenuProps = (): Partial<MenuProps> => ({
  ...getPortalMenuProps(),
});

export const OVERLAY_SAFE_MENU_PROPS: Partial<MenuProps> = getPortalMenuProps();
export const OVERLAY_SAFE_DIALOG_MENU_PROPS: Partial<MenuProps> = getDialogMenuProps();

const mergeWithBaseMenuProps = (
  base: Partial<MenuProps>,
  overrides: Partial<MenuProps> = {}
): Partial<MenuProps> => {
  const baseSlotProps = (base.slotProps || {}) as Record<string, any>;
  const overrideSlotProps = (overrides.slotProps || {}) as Record<string, any>;
  const basePaperProps = (base.PaperProps || {}) as Record<string, any>;
  const overridePaperProps = (overrides.PaperProps || {}) as Record<string, any>;

  return {
    ...base,
    ...overrides,
    style: { ...(base.style || {}), ...(overrides.style || {}) },
    sx: { ...((base.sx || {}) as object), ...((overrides.sx || {}) as object) },
    slotProps: {
      ...baseSlotProps,
      ...overrideSlotProps,
      root: {
        ...(baseSlotProps.root || {}),
        ...(overrideSlotProps.root || {}),
        style: {
          ...(baseSlotProps.root?.style || {}),
          ...(overrideSlotProps.root?.style || {}),
        },
        sx: {
          ...(baseSlotProps.root?.sx || {}),
          ...(overrideSlotProps.root?.sx || {}),
        },
      },
      paper: {
        ...(baseSlotProps.paper || {}),
        ...(overrideSlotProps.paper || {}),
        style: {
          ...(baseSlotProps.paper?.style || {}),
          ...(overrideSlotProps.paper?.style || {}),
        },
        sx: {
          ...(baseSlotProps.paper?.sx || {}),
          ...(overrideSlotProps.paper?.sx || {}),
        },
      },
    },
    PaperProps: {
      ...basePaperProps,
      ...overridePaperProps,
      style: {
        ...(basePaperProps.style || {}),
        ...(overridePaperProps.style || {}),
      },
      sx: {
        ...(basePaperProps.sx || {}),
        ...(overridePaperProps.sx || {}),
      },
    },
  };
};

export const withOverlaySafeMenuProps = (
  overrides: Partial<MenuProps> = {}
): Partial<MenuProps> => mergeWithBaseMenuProps(OVERLAY_SAFE_MENU_PROPS, overrides);

export const withOverlaySafeDialogMenuProps = (
  overrides: Partial<MenuProps> = {}
): Partial<MenuProps> => mergeWithBaseMenuProps(OVERLAY_SAFE_DIALOG_MENU_PROPS, overrides);
